import { SedeModel } from './../../interfaces/sedeModel';
import { Injectable } from "@angular/core";
import { UserModel, UserModelClass } from "../../interfaces/UserModel";
import { ApiService } from "../../service/api.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Roles } from "../../constant/roles";
import { ToastController } from "@ionic/angular";
import { Router } from "@angular/router";
import { UserStore } from "../../store/user.store";
import { environment } from "../../../environments/environment";
import { GoogleUser } from "../../service/google-auth.service";
import { ProvisioningUtentiModel } from '../../interfaces/ProvisioningUtentiModel';
import { AcuStorageService } from '../../service/acustorage.service';
import { JarvisApiService } from '../../service/jarvis.service';
import { decodeJarvisCompanyUnit } from '../../constant/JarvisUnitAziendale';

@Injectable({
  providedIn: "root",
})
export class AuthService {
  authState: any;
  provisioningUtenti: Array<ProvisioningUtentiModel>;

  private acuToken: string;

  acuplaceVersion: string;

  constructor(
    private apiService: ApiService,
    private toastCtrl: ToastController,
    private router: Router,
    private userStore: UserStore,
    private acuStorage: AcuStorageService,
    private jarvisApiService: JarvisApiService
  ) {
    this.acuplaceVersion = environment.acuplaceVersion;
  }

  public getAcuToken() {
    return this.acuToken;
  }

  async login(socialUser: GoogleUser & { roles?: string[], funzione?: string, sediAttive?: any[] }) {
    console.log("login called");

    if (environment.allowedEmails && environment.allowedEmails.length > 0) {
      if (!environment.allowedEmails.includes(socialUser.email)) {
        const toast = await this.toastCtrl.create({
          header: `Accesso non consentito per questa email (${socialUser.email})`,
          duration: 3000,
        });
        await toast.present();
        return;
      }
    }

    let user: UserModel;

    try {
      console.log("Recupero utente per id:", socialUser.id);
      user = await this.apiService.getUserById(socialUser.id);
    } catch (e) {
      console.error("Errore recupero utente:", e);
      if (e instanceof HttpErrorResponse) {
        console.log("Dettagli errore - status:", e.status, "text:", e.statusText);
        if (e.status === 0) {
          console.error("ERRORE CRITICO: Status 0 rilevato. Possibile problema CORS, Proxy o Connessione Interrotta.");
          this.router.navigate(['/service-unavailable']);
          return; // Interrompiamo il login qui, non possiamo procedere al provisioning se la rete non va
        }
      }
    }

    if (user) {
      console.log("User presente, aggiorno il socialUser");

      user.id = socialUser.id;
      user.email = socialUser.email;
      user.firstName = socialUser.firstName;
      user.lastName = socialUser.lastName;
      user.name = socialUser.name;
      user.photoUrl = socialUser.photoUrl;
      user.active = true;
      user.lastAccess = new Date();
      user.lastAccessVersion = this.acuplaceVersion;
      user.environment = window.navigator.userAgent.toLowerCase();

      // Debug override: only apply if the user fields are missing or empty
      if (!environment.production && socialUser.roles && (!user.roles || user.roles.length === 0)) {
        user.roles = socialUser.roles;
      }
      if (!environment.production && socialUser.funzione && (!user.funzione || user.funzione === 'GENERAL')) {
        user.funzione = socialUser.funzione;
      }
      if (!environment.production && socialUser.sediAttive && (!user.sediAttive || user.sediAttive.length === 0)) {
        user.sediAttive = socialUser.sediAttive;
      }

      let mailUtenteName = user.email.substring(0, user.email.lastIndexOf("@"));
      if (environment.administrator.includes(mailUtenteName))
        if (!user.roles.includes(Roles.ADMINISTRATOR))
          user.roles.push(Roles.ADMINISTRATOR);

      this.userStore.updateCurrentUser(user).then(() => {
        const skipTutorial = this.acuStorage.getObjectInStorage('skipLoginTutorial');
        const seenVersion = this.acuStorage.getObjectInStorage<string>('seenTutorialVersion');
        const redirectUrl = this.acuStorage.getAndClearRedirectUrl();
        window.dispatchEvent(new CustomEvent("user:login"));
        if (skipTutorial && seenVersion === this.acuplaceVersion) {
          this.router.navigateByUrl(redirectUrl || "/app/tabs/home");
        } else {
          this.router.navigate(["/tutorial"], { queryParams: { isFirstLogin: true } });
        }
      })
        .catch((err) => {
          console.error("Update user failed", err);
          this.router.navigate(['/service-unavailable']);
        });
    } else {
      console.log(
        "User non presente, verifico l'email ed eventualmente lo aggiungo"
      );

      if (
        (!environment.production) || // Always allow in dev mode for mock profiles
        (environment.trustedDomains &&
          environment.trustedDomains.includes(
            socialUser.email.substring(socialUser.email.lastIndexOf("@"))
          ))
      ) {
        console.log("User non presente, procedo con il provisioning gestito");

        //primo inserimento
        user = new UserModelClass();

        user.id = socialUser.id;
        user.email = socialUser.email;
        user.firstName = socialUser.firstName.trim();
        user.lastName = socialUser.lastName.trim();
        user.name = socialUser.name;
        user.photoUrl = socialUser.photoUrl;
        user.active = true; // Auto-activate for persistent mock/trusted
        user.lastAccess = new Date();
        user.lastAccessVersion = this.acuplaceVersion;
        user.environment = window.navigator.userAgent.toLowerCase();

        if (!environment.production && socialUser.funzione) {
          user.funzione = socialUser.funzione;
        } else {
          user.funzione = "GENERAL";
        }

        if (!environment.production && socialUser.sediAttive) {
          user.sediAttive = socialUser.sediAttive;
        }

        user.roles = new Array<string>();
        if (!environment.production && socialUser.roles) {
          user.roles = socialUser.roles;
        } else {
          user.roles.push(Roles.USER);
        }

        let mailUtenteName = user.email.substring(
          0,
          user.email.lastIndexOf("@")
        );

        if (environment.administrator.includes(mailUtenteName)) {
          if (!user.roles.includes(Roles.ADMINISTRATOR))
            user.roles.push(Roles.ADMINISTRATOR);
        }

        // Persistent insertion into DB
        this.apiService.insertUser(user)
          .then(() => {
            console.log("Provisioning completato su DB per:", user.email);
            this.userStore.updateCurrentUser(user).then(() => {
              const skipTutorial = this.acuStorage.getObjectInStorage('skipLoginTutorial');
              const seenVersion = this.acuStorage.getObjectInStorage<string>('seenTutorialVersion');
              const redirectUrl = this.acuStorage.getAndClearRedirectUrl();
              window.dispatchEvent(new CustomEvent("user:login"));
              if (skipTutorial && seenVersion === this.acuplaceVersion) {
                this.router.navigateByUrl(redirectUrl || "/app/tabs/home");
              } else {
                this.router.navigate(["/tutorial"], { queryParams: { isFirstLogin: true } });
              }
            });
          })
          .catch((err) => {
            console.error("Provisioning failed", err);
            this.router.navigate(['/service-unavailable']);
          });
      } else {
        const toast = await this.toastCtrl.create({
          header: `Attualmente è possibile il login con un dominio consentito (${environment.trustedDomains})`,
          duration: 3000,
        });

        // Present the toast at the bottom of the page
        await toast.present();
      }
    }
  }

  private async _getSedeByCodice(codice: string) {
    const sedi = await this.apiService.getSediDisponibili();
    if (sedi) {
      const sede = sedi.find(elem => elem.codice == codice);
      if (sede) {
        sede.uffici = null;
        sede.dataAggiornamento = null;
        sede.iconColor = null;
        return sede;
      }
    }
    return null;
  }
}
